import DOMPurify from 'dompurify';

// Configure DOMPurify to preserve ReactQuill formatting while maintaining security
const sanitizeConfig = {
  // Allow all common formatting tags that ReactQuill uses
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'a', 'span', 'div',
    'blockquote', 'pre', 'code',
    // ReactQuill specific
    'sub', 'sup', 'font', 'small'
  ],
  // Allow style attributes and ReactQuill classes
  ALLOWED_ATTR: [
    'href', 'target', 'rel',
    'style', 'class', 'id',
    // ReactQuill uses these
    'color', 'background-color', 'font-size', 'font-family',
    'text-align', 'text-decoration', 'text-indent',
    'line-height', 'letter-spacing',
    'font-weight', 'font-style',
    'margin', 'margin-left', 'margin-right', 'margin-top', 'margin-bottom',
    'padding', 'padding-left', 'padding-right', 'padding-top', 'padding-bottom',
    'border', 'border-color', 'border-width', 'border-style',
    'list-style', 'list-style-type'
  ],
  // Allow safe URLs
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  // Only forbid dangerous event handlers
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onfocus', 'onblur', 'onchange', 'onsubmit'],
  // Preserve content structure
  KEEP_CONTENT: true,
  // Allow data URLs for inline images (but limit to safe types)
  ALLOW_DATA_ATTR: false,
};

/**
 * Sanitize HTML content to prevent XSS attacks while preserving formatting
 * @param html - The HTML content to sanitize
 * @returns Sanitized HTML string
 */
export const sanitizeHtml = (html: string | null | undefined): string => {
  if (!html) return '';

  return DOMPurify.sanitize(html, sanitizeConfig);
};

/**
 * Sanitize HTML with very permissive settings for trusted WYSIWYG content
 * @param html - The HTML content to sanitize
 * @returns Sanitized HTML with minimal filtering
 */
export const sanitizeTrustedHtml = (html: string | null | undefined): string => {
  if (!html) return '';

  // Very permissive config for trusted content like admin messages
  return DOMPurify.sanitize(html, {
    ...sanitizeConfig,
    // Allow almost all formatting while blocking only dangerous scripts
    ALLOWED_TAGS: [
      ...sanitizeConfig.ALLOWED_TAGS,
      'img', 'table', 'thead', 'tbody', 'tr', 'td', 'th',
      'hr', 'center', 'left', 'right'
    ],
    ALLOWED_ATTR: [
      ...sanitizeConfig.ALLOWED_ATTR,
      'src', 'alt', 'width', 'height', 'align', 'valign',
      'cellpadding', 'cellspacing', 'border',
      'colspan', 'rowspan'
    ],
  });
};

/**
 * Escape HTML so untrusted text can be safely embedded in markup.
 *
 * Use this BEFORE hand-rolling markdown-ish text into HTML. Regex "markdown to
 * HTML" conversions typically only handle the patterns they care about (**bold**,
 * links, headings) and pass everything else through verbatim — so any real HTML
 * in the source renders live. Escaping first means the only markup in the result
 * is what our own replacements produced.
 *
 * A tag in the source then shows as visible text rather than silently vanishing,
 * which also makes a bad AI response obvious instead of mysterious.
 */
export const escapeHtml = (text: string | null | undefined): string => {
  if (!text) return '';

  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

/**
 * Sanitize a short inline snippet — AI-generated summaries and similar.
 *
 * Far tighter than sanitizeHtml(), which is configured for ReactQuill documents
 * and permits links, divs and classes. Inline copy needs emphasis and nothing
 * else, so anything that could carry behaviour or navigation is dropped.
 */
export const sanitizeInlineHtml = (html: string | null | undefined): string => {
  if (!html) return '';

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['strong', 'b', 'em', 'i', 'br'],
    ALLOWED_ATTR: ['style'],
    ALLOWED_URI_REGEXP: /^$/,
    KEEP_CONTENT: true,
    ALLOW_DATA_ATTR: false,
  });
};

/**
 * Sanitize HTML and ensure external links open in new tabs
 * @param html - The HTML content to sanitize
 * @returns Sanitized HTML with safe link behavior
 */
export const sanitizeHtmlWithLinks = (html: string | null | undefined): string => {
  if (!html) return '';

  // First sanitize the HTML
  const sanitized = sanitizeTrustedHtml(html);

  // Then add target="_blank" and rel="noopener noreferrer" to external links
  return sanitized.replace(
    /<a(\s+[^>]*?)href=["'](https?:\/\/[^"']+)["']([^>]*?)>/gi,
    '<a$1href="$2" target="_blank" rel="noopener noreferrer"$3>'
  );
};